import { Component, useEffect, type ReactNode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { router, entry } from '@/routes';

type InventoryRoute = {
  pattern: string;
  path: string;
  title: string;
  page: string;
  standalone: boolean;
  isPublic: boolean;
  entersFrom?: string;
};

declare global {
  var __onautoRouteInventory: { routes: InventoryRoute[]; entry: typeof entry } | undefined;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <main className="mx-auto max-w-lg px-6 py-24">
          <h1 className="text-3xl font-bold">The departure board couldn't open.</h1>
          <p className="mt-3 text-muted-foreground">Please reload to check bus times again.</p>
        </main>
      );
    }
    return this.props.children;
  }
}

export function App() {
  useEffect(() => {
    document.getElementById('root')?.setAttribute('data-app-ready', '1');
    const routes = Object.values(router.routesById)
      .filter((route) => route.id !== '__root__')
      .map((route) => {
        const path = route.fullPath;
        const data = route.options.staticData;
        return {
          pattern: path.replace(/\$([^/]+)/g, '[$1]'),
          path,
          title: data?.title ?? '',
          page: data?.page ?? '',
          standalone: data?.standalone ?? false,
          isPublic: data?.isPublic ?? false,
          ...(data?.entersFrom ? { entersFrom: data.entersFrom } : {}),
        };
      });
    globalThis.__onautoRouteInventory = { routes, entry };
    return () => {
      document.getElementById('root')?.removeAttribute('data-app-ready');
      globalThis.__onautoRouteInventory = undefined;
    };
  }, []);

  return (
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  );
}
